import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import {
  PayPalButtons,
  PayPalCardFieldsForm,
  PayPalCardFieldsProvider,
  PayPalScriptProvider,
  usePayPalCardFields,
} from "@paypal/react-paypal-js";

import DonationForm from "./DonationForm";
import RecentDonations from "./RecentDonations";
import ZimswitchPaymentForm from "./ZimswitchPaymentForm";
import EftpayPaymentForm from "./EftpayPaymentForm";
import { fireDonationConfetti } from "./confetti";
import type { FormData, FormErrors, Donation } from "./types";
import {
  PAYPAL_METHOD_IDS,
  ZIMSWITCH_METHOD_IDS,
  EFTPAY_CARD_METHOD_IDS,
  EFTPAY_ECOCASH_METHOD_IDS,
  STORAGE_KEY,
} from "./types";
import {
  capturePayPalChemaDonation,
  initiatePayPalChemaDonation,
} from "./donationsApi";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

function PayCardButton({
  disabled,
  onError,
}: {
  disabled: boolean;
  onError: (msg: string) => void;
}) {
  const { cardFieldsForm } = usePayPalCardFields();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (!cardFieldsForm) {
      onError(
        "Card form is still loading — please wait a moment and try again.",
      );
      return;
    }
    setBusy(true);
    try {
      const state = await cardFieldsForm.getState();
      if (!state.isFormValid) {
        onError("Please fill in all card details correctly.");
        setBusy(false);
        return;
      }
      await cardFieldsForm.submit();
      // The provider's onApprove will fire after a successful submit.
    } catch (e) {
      onError((e as Error).message || "Card payment failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      fullWidth
      variant="contained"
      onClick={handleClick}
      disabled={disabled || busy}
      sx={{
        mt: 1.5,
        borderRadius: "999px",
        backgroundColor: "#111827",
        "&:hover": { backgroundColor: "#000" },
      }}
    >
      {busy ? "Processing..." : "Pay with card"}
    </Button>
  );
}

function Form() {
  const params = useParams<{ id?: string; pageType?: string; fullName?: string }>();
  const streamId = params.id ? Number(params.id) : null;
  const pageType = params.pageType || "memorial-page";
  const fullName = params.fullName || "";

  const [formData, setFormData] = useState<FormData>({
    currency: "USD",
    amount: "",
    email: "",
    paymentMethod: "ecocash",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recentDonations, setRecentDonations] = useState<Donation[]>([]);

  // PayPal-specific state
  const [paypalDonationId, setPaypalDonationId] = useState<number | null>(null);
  const [paypalInfo, setPaypalInfo] = useState<string | null>(null);
  const [paypalError, setPaypalError] = useState<string | null>(null);

  const isPayPal = PAYPAL_METHOD_IDS.has(formData.paymentMethod);
  const isZimswitch = ZIMSWITCH_METHOD_IDS.has(formData.paymentMethod);
  const isEftpayCard = EFTPAY_CARD_METHOD_IDS.has(formData.paymentMethod);
  const isEftpayEcocash = EFTPAY_ECOCASH_METHOD_IDS.has(formData.paymentMethod);
  const isEftpay = isEftpayCard || isEftpayEcocash;

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;

    type StoredDonation = Omit<Donation, "date"> & { date: string };

    try {
      const donations = (JSON.parse(stored) as StoredDonation[]).map(
        (d): Donation => ({ ...d, date: new Date(d.date) }),
      );
      setRecentDonations(donations);
    } catch (error) {
      console.error("Error loading donations from storage:", error);
    }
  }, []);

  useEffect(() => {
    if (recentDonations.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recentDonations));
    }
  }, [recentDonations]);

  // Clear any PayPal status when the user switches away from PayPal.
  useEffect(() => {
    if (!isPayPal) {
      setPaypalError(null);
      setPaypalInfo(null);
      setPaypalDonationId(null);
    }
  }, [isPayPal]);

  const validateEmail = (email: string): boolean => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateAmount = (amount: string): boolean => {
    if (!amount.trim()) return false;
    const numAmount = parseFloat(amount);
    return !isNaN(numAmount) && numAmount > 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isPayPal || isZimswitch || isEftpay) {
      // PayPal, ZimSwitch and EFT PAY have their own hosted forms — nothing to
      // submit here.
      return;
    }

    const newErrors: FormErrors = {};
    if (!validateAmount(formData.amount)) {
      newErrors.amount = "Please enter a valid donation amount";
    }
    if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      // TODO: wire actual Paynow redirect here.
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const newDonation: Donation = {
        id: Date.now().toString(),
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        email: formData.email.trim(),
        paymentMethod: formData.paymentMethod,
        status: "Pending",
        date: new Date(),
      };
      setRecentDonations((prev) => [newDonation, ...prev]);
      setFormData({
        currency: "USD",
        amount: "",
        email: "",
        paymentMethod: formData.paymentMethod,
      });
    } catch (error) {
      console.error("Error processing donation:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const numericAmount = useMemo(() => {
    const n = parseFloat(formData.amount);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [formData.amount]);

  const validatePayPalInputs = (): boolean => {
    const newErrors: FormErrors = {};
    if (!validateAmount(formData.amount)) {
      newErrors.amount = "Please enter a valid donation amount";
    }
    if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createPayPalOrder = async (): Promise<string> => {
    setPaypalError(null);
    setPaypalInfo(null);
    if (!validatePayPalInputs() || !numericAmount) {
      throw new Error("Please enter a valid amount before paying with PayPal.");
    }
    const res = await initiatePayPalChemaDonation({
      amount: numericAmount,
      email: formData.email.trim() || undefined,
      stream_id: streamId,
    });
    setPaypalDonationId(res.donation_id);
    return res.order_id;
  };

  const capturePayPalApproved = async () => {
    const donationId = paypalDonationId;
    if (!donationId || !numericAmount) return;
    try {
      const r = await capturePayPalChemaDonation(donationId);
      if (r.success) {
        fireDonationConfetti();
        setPaypalInfo("Payment confirmed. Thank you for your donation!");
        setRecentDonations((prev) => [
          {
            id: String(donationId),
            amount: numericAmount,
            currency: "USD",
            email: formData.email.trim(),
            paymentMethod: "paypal",
            status: "Completed",
            date: new Date(),
          },
          ...prev,
        ]);
        setFormData((prev) => ({
          ...prev,
          amount: "",
          email: "",
        }));
        setPaypalDonationId(null);
      } else {
        setPaypalError(`Payment status: ${r.status}`);
      }
    } catch (e) {
      setPaypalError((e as Error).message || "Capture failed.");
    }
  };

  const paypalActionSlot = isPayPal ? (
    PAYPAL_CLIENT_ID ? (
      <Box>
        {paypalInfo && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {paypalInfo}
          </Alert>
        )}
        {paypalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {paypalError}
          </Alert>
        )}
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Pay with your PayPal balance, or enter your card details below (Visa,
          Mastercard, Amex).
        </Typography>

        <PayPalScriptProvider
          options={{
            clientId: PAYPAL_CLIENT_ID,
            currency: "USD",
            components: "buttons,card-fields",
            "enable-funding": "card",
          }}
        >
          <PayPalButtons
            style={{ layout: "vertical", label: "donate" }}
            disabled={!numericAmount}
            createOrder={createPayPalOrder}
            onApprove={capturePayPalApproved}
            onError={(e) => {
              const msg =
                typeof (e as { message?: unknown })?.message === "string"
                  ? (e as { message: string }).message
                  : "PayPal error";
              setPaypalError(msg);
            }}
          />

          <Box
            sx={{
              my: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              color: "text.secondary",
              fontSize: 12,
            }}
          >
            <Divider sx={{ flex: 1 }} />
            <span>or pay with card</span>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <PayPalCardFieldsProvider
            createOrder={createPayPalOrder}
            onApprove={capturePayPalApproved}
            onError={(e: unknown) => {
              const msg =
                typeof (e as { message?: unknown })?.message === "string"
                  ? (e as { message: string }).message
                  : "Card payment failed.";
              setPaypalError(msg);
            }}
            style={{
              input: {
                "font-size": "14px",
                color: "#111827",
                padding: "10px 12px",
              },
              ".invalid": { color: "#dc2626" },
            }}
          >
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                p: 1.5,
              }}
            >
              <PayPalCardFieldsForm />
            </Box>
            <PayCardButton
              disabled={!numericAmount}
              onError={(msg) => setPaypalError(msg)}
            />
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, color: "text.secondary" }}
            >
              Card details are processed securely by PayPal. We never see your
              card number, expiry, or CVV.
            </Typography>
          </PayPalCardFieldsProvider>
        </PayPalScriptProvider>
      </Box>
    ) : (
      <Alert severity="warning">
        PayPal is not configured. Set <code>REACT_APP_PAYPAL_CLIENT_ID</code> in
        the frontend <code>.env</code> file to enable it.
      </Alert>
    )
  ) : undefined;

  const paypalFooterText = isPayPal ? (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      Secure payments powered by PayPal. PayPal supports USD only.
    </Typography>
  ) : undefined;

  const zimswitchActionSlot = isZimswitch ? (
    <ZimswitchPaymentForm
      amount={numericAmount}
      currency={formData.currency}
      email={formData.email}
      streamId={streamId}
      pageType={pageType}
      fullName={fullName}
      onValidate={validatePayPalInputs}
    />
  ) : undefined;

  const zimswitchFooterText = isZimswitch ? (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      Secure payments powered by ZimSwitch Online (NMB). USD and ZWL supported.
    </Typography>
  ) : undefined;

  const eftpayActionSlot = isEftpay ? (
    <EftpayPaymentForm
      channel={isEftpayEcocash ? "ecocash" : "card"}
      amount={numericAmount}
      // Cards settle in USD; EcoCash follows the selected currency (USD/ZWG).
      currency={isEftpayEcocash ? formData.currency : "USD"}
      email={formData.email}
      streamId={streamId}
      pageType={pageType}
      fullName={fullName}
      onValidate={validatePayPalInputs}
    />
  ) : undefined;

  const eftpayFooterText = isEftpay ? (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {isEftpayEcocash
        ? "Secure EcoCash payments powered by EFT PAY (NMBZ). USD and ZWG supported."
        : "Secure card payments powered by EFT PAY (NMBZ). USD only."}
    </Typography>
  ) : undefined;

  const actionSlot =
    paypalActionSlot ?? eftpayActionSlot ?? zimswitchActionSlot;
  const footerText =
    paypalFooterText ?? eftpayFooterText ?? zimswitchFooterText;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
        py: { xs: 6, sm: 8 },
        px: { xs: 2, sm: 3, lg: 4 },
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Support Us with a Donation
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", mx: "auto", maxWidth: 600 }}
            >
              Your generous contribution helps us continue providing
              compassionate services to families across Zimbabwe and beyond.
            </Typography>
          </Box>
        </motion.div>

        <Paper
          elevation={0}
          sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2 }}
        >
          <DonationForm
            formData={formData}
            errors={errors}
            isSubmitting={isSubmitting}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            actionSlot={actionSlot}
            footerText={footerText}
          />
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <RecentDonations donations={recentDonations} />
        </Paper>
      </Container>
    </Box>
  );
}

export default Form;
